import React, { useMemo } from 'react';
import type { HeatmapCell, HeatmapResponse } from '@/api/narrative';
import { engagementColor } from './colors';

export interface HeatmapGridProps {
  data: HeatmapResponse;
  onSelectCell: (cell: HeatmapCell) => void;
  selectedKey?: string | null;
}

function formatPeriodHeader(period: string): string {
  const d = new Date(period);
  const currentYear = new Date().getFullYear().toString();
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: period.startsWith(currentYear) ? undefined : '2-digit',
  });
}

/**
 * The sticky-header table that renders one cell per (precinct, period).
 *
 * - Cells are `<button>` elements so keyboard focus + Enter/Space works
 *   out of the box — no custom key handling needed in the parent.
 * - Missing cells (suppressed by K-anonymity at the backend) render as a
 *   dim em dash with an explanatory title.
 * - The `data-testid="engagement-cell"` attribute is preserved for the
 *   existing Playwright suite.
 */
export function HeatmapGrid({ data, onSelectCell, selectedKey }: HeatmapGridProps) {
  const { cellsByKey, precinctList, periodList } = useMemo(() => {
    const cellsByKey = new Map<string, HeatmapCell>();
    for (const cell of data.cells || []) {
      cellsByKey.set(`${cell.precinct_num}|${cell.period_start}`, cell);
    }
    const precinctList = [...new Set(data.precincts || [])].sort();
    const periodList = [...new Set(data.periods || [])].sort();
    return { cellsByKey, precinctList, periodList };
  }, [data]);

  return (
    <div
      className="bg-bg-elevated rounded-lg border border-border-base overflow-auto"
      data-testid="heatmap-grid"
    >
      <table
        className="text-xs border-collapse w-full"
        aria-label="Engagement heatmap: precinct by time period"
      >
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky left-0 top-0 z-20 bg-bg-base border border-border-base px-3 py-2 text-fg-secondary font-semibold text-left"
              style={{ minWidth: '120px' }}
            >
              Precinct
            </th>
            {periodList.map((period) => (
              <th
                scope="col"
                key={period}
                className="sticky top-0 z-10 bg-bg-base border border-border-base px-2 py-2 text-fg-secondary font-semibold text-center whitespace-nowrap"
                style={{ minWidth: '100px' }}
              >
                {formatPeriodHeader(period)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {precinctList.map((precinct) => (
            <tr key={precinct} data-testid="heatmap-row">
              <th
                scope="row"
                className="sticky left-0 z-10 bg-bg-base border border-border-base px-3 py-2 text-fg-primary font-semibold text-left"
                style={{ minWidth: '120px' }}
              >
                {precinct}
              </th>
              {periodList.map((period) => {
                const key = `${precinct}|${period}`;
                const cell = cellsByKey.get(key);

                if (!cell) {
                  return (
                    <td
                      key={key}
                      className="border border-border-base px-2 py-2 text-center bg-bg-raised/20 text-fg-muted"
                      style={{ minWidth: '100px' }}
                      title="Suppressed (fewer than min_n knocks)"
                    >
                      —
                    </td>
                  );
                }

                const { bg, text } = engagementColor(cell.engagement_score);
                const isZeroConvo =
                  cell.conversations === 0 ||
                  !Number.isFinite(cell.engagement_score);
                const isSelected = key === selectedKey;

                const tooltip = [
                  `${cell.precinct_num} · ${new Date(
                    period
                  ).toLocaleDateString()}`,
                  `Knocks: ${cell.knocks_attempted}`,
                  `Conversations: ${cell.conversations}`,
                  `Positive leaning: ${cell.positive_leaning}`,
                  `Engagement: ${(cell.engagement_score * 100).toFixed(1)}%`,
                ].join('\n');

                return (
                  <td
                    key={key}
                    className={`border border-border-base p-0`}
                    style={{ minWidth: '100px' }}
                  >
                    <button
                      type="button"
                      data-testid="engagement-cell"
                      aria-label={`Precinct ${cell.precinct_num} on ${new Date(
                        period
                      ).toLocaleDateString()}: ${cell.knocks_attempted} knocks, ${cell.conversations} conversations`}
                      onClick={() => onSelectCell(cell)}
                      title={tooltip}
                      className={`w-full h-full min-h-[44px] px-2 py-2 text-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent hover:ring-2 hover:ring-accent transition ${bg} ${text} ${
                        isSelected ? 'ring-2 ring-emerald-300' : ''
                      }`}
                    >
                      <span className="font-bold tabular-nums">
                        {isZeroConvo
                          ? '—'
                          : (cell.engagement_score * 100).toFixed(0)}
                      </span>
                      {!isZeroConvo && (
                        <div className="text-[10px] mt-0.5 tabular-nums">
                          {cell.conversations}/{cell.knocks_attempted}
                        </div>
                      )}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
